import React, {ChangeEvent, useMemo, useState} from 'react';
import {EBirdChecklistByFamily} from "ebird-mydata-reader";
import {FamilyExplorerCard} from "../../../components/family-explorer-card";
import {toRootRelativeImagePath} from "../../../helpers/imagePaths";
import {
    ChecklistFilter,
    getFamilyCommonName,
    getFamilyScientificName,
    getFamilyStats
} from "../helpers";
import './family-list.scss';

export type FamilyListProps = {
    checklist: EBirdChecklistByFamily[],
    checklistStats: {
        seenFamilies: number,
        unseenFamilies: number,
        extinctFamilies: number
    },
    onChangeFamily: (family: EBirdChecklistByFamily) => void
}

export function FamilyList({checklist, checklistStats, onChangeFamily}: FamilyListProps) {
    const [activeFilter, setActiveFilter] = useState<ChecklistFilter>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const visibleFamilies = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return checklist.filter(family => {
            const familyStats = getFamilyStats(family);

            if (activeFilter === 'seen' && (!family.seen || familyStats.isExtinct)) return false;
            if (activeFilter === 'unseen' && (family.seen || familyStats.isExtinct)) return false;
            if (activeFilter === 'extinct' && !familyStats.isExtinct) return false;
            if (!normalizedSearch) return true;

            return `${family.familyName} ${family.familyScientific} ${family.familyCommon}`.toLowerCase().includes(normalizedSearch);
        });
    }, [activeFilter, checklist, searchTerm]);

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    }

    return (
        <>
            <div className="checklist__toolbar">
                <div className="checklist__tabs" aria-label="Checklist filters">
                    <button
                        type="button"
                        className={`checklist__tab ${activeFilter === 'all' ? 'is-active' : ''}`}
                        onClick={() => setActiveFilter('all')}
                    >
                        All Families
                    </button>
                    <button
                        type="button"
                        className={`checklist__tab ${activeFilter === 'seen' ? 'is-active' : ''}`}
                        onClick={() => setActiveFilter('seen')}
                    >
                        Seen ({checklistStats.seenFamilies.toLocaleString()})
                    </button>
                    <button
                        type="button"
                        className={`checklist__tab ${activeFilter === 'unseen' ? 'is-active' : ''}`}
                        onClick={() => setActiveFilter('unseen')}
                    >
                        Not Seen ({checklistStats.unseenFamilies.toLocaleString()})
                    </button>
                    <button
                        type="button"
                        className={`checklist__tab ${activeFilter === 'extinct' ? 'is-active' : ''}`}
                        onClick={() => setActiveFilter('extinct')}
                    >
                        Extinct ({checklistStats.extinctFamilies.toLocaleString()})
                    </button>
                </div>
                <label className="checklist__search">
                    <span>Search families</span>
                    <input
                        type="search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search families..."
                    />
                </label>
            </div>

            {visibleFamilies.length ?
                <ul className="checklist-list">
                    {visibleFamilies.map(family => {
                        const familyStats = getFamilyStats(family);

                        return (
                            <li className="checklist-list__item" key={family.familyName}>
                                <FamilyExplorerCard
                                    subject={family}
                                    seen={family.seen}
                                    title={getFamilyScientificName(family)}
                                    alt={getFamilyCommonName(family)}
                                    subtitle={getFamilyCommonName(family)}
                                    statusLabel={familyStats.isExtinct ? 'Extinct' : undefined}
                                    statusVariant={familyStats.isExtinct ? 'extinct' : undefined}
                                    statCount={`${familyStats.seenSpecies} / ${familyStats.totalSpecies} species`}
                                    img={toRootRelativeImagePath(family.speciesList[0]?.image?.medium ?? family.speciesList[0]?.image?.thumb) || '/assets/fallback-bird.svg'}
                                    statProgress={familyStats.totalSpecies ? familyStats.seenSpecies / familyStats.totalSpecies : 0}
                                    onClick={onChangeFamily}
                                />
                            </li>
                        );
                    })}
                </ul> :
                <div className="checklist__empty-state">
                    <strong>No families found</strong>
                    <span>Try a different search or switch family filters.</span>
                </div>
            }
        </>
    );
}
